/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, Node, KeyCodes, onTelemetryClick, getPayloadObject, INodeProps, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { AddToCartBehavior } from '@msdyn365-commerce/components';
import * as React from 'react';
import classnames from 'classnames';

import { ICartIconViewProps } from './dlvry-cart-icon';
import { IFlyoutCartLineItemViewProps } from './components/flyout-cart-line-items';
import CartLineItemComponent from '../../themes/dlvry-adventureworks/views/components/cartlineitem.component';
import CartIconComponent from '../../themes/dlvry-adventureworks/views/components/carticon.component';
import { _mergeDuplicateCartlines } from '../../shared-utils/cart-utils';

const renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): JSX.Element[] | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;

        // Component override stopped working in mini-cart after 9.48, force it here
        const cartLineProps = (cartLine.cartline as React.ReactElement).props;
        const cartLineComp = <CartLineItemComponent {...cartLineProps} />;

        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>
                    {cartLineComp}
                    {/* {cartLine.cartline} */}
                </Node>
                {cartLine.storeLocation}
                {cartLine.remove}
            </Node>
        );
    });
};

const renderTotalPrice = (props: ICartIconViewProps): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    // Calculate total price from cart lines instead of cart object
    const sumPrice = props.data.cart.result?.cart.CartLines
        ?.map(x => (x.NetAmountWithoutTax || (x.Price! * (x.Quantity || 1))))
        .reduce((sum, price) => sum + price, 0);
    const sumPriceFormatString = sumPrice
        ? props.context.cultureFormatter.formatCurrency(sumPrice)
        : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);

    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {props.resources.totalPriceFormatString}
                <span className='ms-cart-icon__subTotalPrice'>{sumPriceFormatString}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

const getModalContainerProps = (isModalOpen: boolean, closeModal: () => void): INodeProps => {
    return {
        tag: Modal,
        placement: 'bottom-end',
        hideArrow: true,
        className: 'ms-cart-icon__minicartmodal-container',
        wrapClassName: 'ms-cart-icon__minicartmodal',
        isOpen: isModalOpen,
        toggle: closeModal
    };
};

const CartIconView: React.FC<ICartIconViewProps> = (props: ICartIconViewProps) => {
    const cartIconRef = React.useRef<HTMLButtonElement>(null);
    const isAutoDisplayTriggered = React.useRef<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const [filter, setFilter] = React.useState<string>('');
    const [showEmptyCartConfirmation, setShowEmptyCartConfirmation] = React.useState<boolean>(false);

    React.useEffect(() => {
        const escHandler = (event: KeyboardEvent) => {
            if (event.keyCode === KeyCodes.Escape) {
                if (showEmptyCartConfirmation) {
                    setShowEmptyCartConfirmation(false);
                } else if (isModalOpen) {
                    cartIconRef.current?.focus();
                    setIsModalOpen(false);
                }
            }
        };

        window.addEventListener('keydown', escHandler, false);

        return () => {
            window.removeEventListener('keydown', escHandler, false);
        };
    }, [isModalOpen, setIsModalOpen, cartIconRef, showEmptyCartConfirmation, setShowEmptyCartConfirmation]);

    const cart = props.data.cart.result ?? undefined;
    const shouldShowMiniCart =
            cart?.isProductAddedToCart && props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;

    if (shouldShowMiniCart) {
        if (!isAutoDisplayTriggered.current) {
            // First time trigger auto mini cart.
            setIsModalOpen(true);
            isAutoDisplayTriggered.current = true;
        } else if (!isModalOpen) {
            // If modal is closed, reset the flag.
            isAutoDisplayTriggered.current = false;
        }
    }

    const totalItemsInCart = React.useMemo(() => {
        return cart?.cart.CartLines ? _mergeDuplicateCartlines(cart.cart.CartLines).length : 0;
    }, [cart?.cart.CartLines]);
    const goToCartButtonText = props.resources.goToCartButtonTitle.replace('{count}', totalItemsInCart.toString());

    const _openModal = React.useCallback(() => {
        const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(props.telemetryContent!, payLoad, 'cart-icon');
        setIsModalOpen(true);
    }, [props.telemetryContent, setIsModalOpen]);

    const _closeModal = React.useCallback(() => {
        cartIconRef.current?.focus();
        setIsModalOpen(false);
    }, [cartIconRef, setIsModalOpen]);

    const doFilter = React.useCallback((cartLine: IFlyoutCartLineItemViewProps) => {
        if (!filter) {
            return true;
        }

        const product = cartLine.data.product;

        return product.Name?.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
            product.ItemId?.toLocaleLowerCase().includes(filter.toLocaleLowerCase());
    }, [filter]);

    return (
        <Node {...props.miniCartWrapper} {...props.renderModuleAttributes(props)}>
            <button onClick={_openModal} className='ms-cart-icon-wrapper msc-btn' ref={cartIconRef}>
                {<CartIconComponent
                    cartLabel={props.resources.cartLabel}
                    cartQtyLabel={props.resources.cartQtyLabel}
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    telemetryContent={props.telemetryContent}
                    data={{ cart: props.data.cart.result }}
                />}
            </button>
            <Node {...getModalContainerProps(isModalOpen, _closeModal)}>
                <Node className='ms-cart-icon__header msc-modal__header'>
                    {props.flyoutTitle}
                    <Node className='ms-cart-icon__count'>{totalItemsInCart} {props.resources.items}</Node>
                    <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={_closeModal} />
                </Node>
                <Node className='ms-cart-icon__body msc-modal__body'>
                    {cart?.isEmpty
                        ? props.slots.emptyCart
                        : (
                            <>
                                <div>
                                    <input
                                        className='ms-cart-icon__filter-input'
                                        data-nomodalcss
                                        type='text'
                                        placeholder={props.resources.searchPlaceholderText}
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                    />
                                </div>

                                <div style={{ marginTop: '10px', textAlign: 'right' }}>
                                    <button id='empty-cart' className='ms-cart-icon__empty-cart msc-btn' onClick={async () => {
                                        setShowEmptyCartConfirmation(true);
                                    }}>
                                        {props.resources.emptyCartButtonText}
                                    </button>
                                </div>
                            </>
                        )}
                    <Node {...props.CartlinesWrapper}>
                        {renderCartlines(props.cartlines?.filter(doFilter), props)}
                    </Node>
                </Node>
                <Node className='ms-cart-icon__footer msc-modal__footer'>
                    {props.slots.promoContentItem}
                    {renderTotalPrice(props)}
                    <p className='ms-cart-icon__subtotal-message'>{props.resources.subTotalMessage}</p>
                    {/* Removed per Task 4253
                    {props.checkoutAsSignInUserButton &&
                        React.cloneElement(props.checkoutAsSignInUserButton as React.ReactElement,{disabled:hasError})} */}
                    {props.checkoutAsGuestButton}
                    {props.goToCartButton && React.cloneElement(props.goToCartButton as React.ReactElement, {
                        'children': goToCartButtonText
                    })}
                    {/* Removed per Task 5216 */}
                    {/* <Node className='ms-cart-icon__btn-section'>
                        {props.data.cart.result?.isEmpty ? (
                            <Button
                                className='ms-cart-icon__btn-backtoshopping'
                                href={getUrlSync('home', props.context.actionContext)}
                            >
                                {props.resources.continueShoppingButtonTitle}
                            </Button>
                        ) : null}
                    </Node> */}
                </Node>
            </Node>

            {showEmptyCartConfirmation && (
                <Modal
                    modalClassName='ms-cart-icon__empty-cart__modal'
                    backdropClassName='ms-cart-icon__empty-cart__modal__backdrop'
                    isOpen={showEmptyCartConfirmation}
                    toggle={() => setShowEmptyCartConfirmation(!showEmptyCartConfirmation)}
                    fade={false}
                >
                    <ModalHeader>
                        <h6 className='ms-cart-icon__empty-cart__modal__header'>{props.resources.emptyCartButtonText}</h6>
                    </ModalHeader>
                    <ModalBody>
                        <div className='ms-cart-icon__empty-cart__modal__description'>{props.resources.emptyCartDescriptionText}</div>
                    </ModalBody>
                    <ModalFooter>
                        <Button className='ms-cart-icon__empty-cart__modal__confirm' onClick={async () => {
                            if (!cart?.cart) {
                                return;
                            }

                            await cart?.removeCartLines({ cartLineIds: cart?.cart.CartLines!.map(x => x.LineId!) });
                            setShowEmptyCartConfirmation(false);
                        }}>
                            {props.resources.emptyCartConfirmButtonText}
                        </Button>
                        <Button className='ms-cart-icon__empty-cart__modal__cancel' onClick={() => setShowEmptyCartConfirmation(false)}>
                        {props.resources.emptyCartCancelButtonText}
                        </Button>
                    </ModalFooter>
                </Modal>
            )}
        </Node>
    );
};

export default CartIconView;
